'use client'

import { Flex, Pagination as MantinePagination, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'

/**
 * HomepageV2 table/card pagination in two data shapes (WWW-5083):
 *
 * - Known total (pass `totalRecords`; crypto = offset API with a total,
 *   baskets = full list fetched once): the real last page is knowable from
 *   the first render, so the count line always reads "start - end of total",
 *   the window clamps to the real last page, and the last page renders as a
 *   boundary button (desktop) whenever the window is short of it — "…" only
 *   bridges a gap of 2+ pages, and vanishes once the window reaches the end.
 * - Unknown total (omit `totalRecords`; stock/prediction = cursor APIs):
 *   the variant below — sliding window with a trailing "…" until
 *   `hasMore === false` reveals the real last page.
 *
 * A list that only fills one page renders no pagination at all — controls and
 * count line both disappear (WWW-5095). Known-total decides from the real page
 * count; unknown-total can only tell once the backend has said there is no
 * next page while we are still on page 1 (reaching page ≥ 2 already proves the
 * list has more than one page).
 *
 * Sliding window rules (shared by both shapes):
 *
 * Page → label rule:
 *   Desktop (≥ medium) keeps a 2-back/3-forward sliding window:
 *     currentPage = 1  →  < 1 2 3 4 … >
 *     currentPage = 3  →  < 1 2 3 4 5 6 … >
 *     currentPage = 10 →  < 1 … 8 9 10 11 12 13 … >
 *     at last page     →  < 1 … 8 9 10 >     (next arrow disabled)
 *   Mobile (< medium): both shapes stack — controls row on top, count line
 *   below (WWW-5152). Known-total keeps only three page buttons — page 1,
 *   the current page and the real last page — with "…" bridging the gaps:
 *     currentPage = 1   →  < 1 … {tail} >
 *     currentPage = 100 →  < 1 … 100 … {tail} >
 *     at last page      →  < 1 … {tail} >
 *   Unknown-total keeps page 1 and the current page with a leading "…" once
 *   they're not adjacent (WWW-5095):
 *     currentPage = 1   →  < 1 … >
 *     currentPage = 100 →  < 1 … 100 … >   (101 not loaded yet)
 *     at last page      →  < 1 … 200 >     (trailing "…" dropped)
 */
interface CursorPaginationProps {
  /** 1-indexed current page. */
  readonly currentPage: number
  /**
   * Number of pages already loaded into memory. Used to compute the trailing
   * "…" state and the real last page in the unknown-total shape; with
   * `totalRecords` present it acts as a fallback only.
   */
  readonly loadedPageCount: number
  /**
   * Known record count (offset API / full-list fetch). When present the
   * pagination renders the real last page and "of total" from the first
   * render. Leave undefined for cursor-backed lists (stock/prediction).
   */
  readonly totalRecords?: number
  /**
   * `false` once the backend says there's no more data. Unknown-total shape
   * only — ignored when `totalRecords` is set. Omitted means "more pages
   * may exist", which is the safe default for cursor lists.
   */
  readonly hasMore?: boolean
  /** True while any chunk fetch is in flight — disables the controls. */
  readonly isFetching: boolean
  readonly recordsPerPage: number
  readonly onChange: (page: number) => void | Promise<void>
  /**
   * i18n namespace for the record-count line (`pagination`/`paginationRange`
   * keys). All HomepageV2 consumers keep the default `coinTable`.
   */
  readonly translationNamespace?: string
}

const SIBLINGS_BACK = 2
const SIBLINGS_FORWARD = 3

type Visibility = 'always' | 'desktop' | 'mobile'
type PageItem =
  | { kind: 'page'; value: number; show: Visibility }
  | { kind: 'dots'; id: 'left' | 'right' | 'm-left' | 'm-right'; show: Visibility }

/**
 * WWW-5095: a list that only fills one page renders no pagination at all —
 * controls and count line both go. Known-total reads the real page count;
 * unknown-total needs the backend to have said "no next page" while we are
 * still on page 1 (having reached page ≥ 2 already proves there is more than
 * one page).
 */
function shouldHidePagination(
  knownLastPage: number | null,
  currentPage: number,
  hasMore: boolean | undefined,
): boolean {
  if (knownLastPage !== null) return knownLastPage <= 1
  return currentPage === 1 && hasMore === false
}

/**
 * Known-total mobile window: only three page buttons — pinned page 1, the
 * current page and the real last page — with "…" bridging each gap of 2+
 * pages. Renders `< 1 … {cur} … {tail} >`; when the current page is (next
 * to) page 1 or the last page it collapses to the deduped buttons.
 */
function buildMobilePageItems(currentPage: number, lastPage: number): PageItem[] {
  const items: PageItem[] = [{ kind: 'page', value: 1, show: 'mobile' }]
  if (currentPage > 2) items.push({ kind: 'dots', id: 'm-left', show: 'mobile' })
  if (currentPage !== 1 && currentPage !== lastPage) {
    items.push({ kind: 'page', value: currentPage, show: 'mobile' })
  }
  if (lastPage - currentPage > 1) items.push({ kind: 'dots', id: 'm-right', show: 'mobile' })
  if (lastPage > 1) items.push({ kind: 'page', value: lastPage, show: 'mobile' })
  return items
}

export default function CursorPagination({
  currentPage,
  loadedPageCount,
  totalRecords,
  hasMore,
  isFetching,
  recordsPerPage,
  onChange,
  translationNamespace = 'coinTable',
}: CursorPaginationProps) {
  const t = useTranslations(translationNamespace)

  // Known-total shape: the real last page is knowable immediately. Otherwise
  // it is only knowable once the backend says no more.
  const totalPages = totalRecords != null ? Math.ceil(totalRecords / recordsPerPage) : null
  const knownLastPage = totalPages ?? (hasMore === false ? loadedPageCount : null)

  if (shouldHidePagination(knownLastPage, currentPage, hasMore)) return null

  // Window around current page. Page 1 is pinned: the window either starts at
  // 1 or at ≥3 (with a "…" between 1 and the window). The window only shows
  // pages that are actually loaded — clicking an unloaded page can't succeed,
  // so we don't render its number.
  let windowStart = Math.max(1, currentPage - SIBLINGS_BACK)
  if (windowStart === 2) windowStart = 1 // avoid rendering "1 2 3 4" as "1 … 2 3 4"

  const furthestReachable = knownLastPage ?? loadedPageCount
  const windowEnd = Math.min(currentPage + SIBLINGS_FORWARD, furthestReachable)

  // WWW-5095: mobile (< medium) keeps page 1 and the current page; the rest
  // of the sliding window is desktop-only. A leading "…" appears on mobile
  // once the current page isn't adjacent to 1, and on desktop once the window
  // starts past 1 (after the windowStart fixup).
  const mobilePages = new Set([1, currentPage])

  // Once a real last page exists (known-total from the start, or a cursor
  // list the backend exhausted), render it as a boundary button and a "…"
  // only when the window is ≥2 pages short of it (an adjacent last page
  // renders as a plain number, no dots).
  const showTrailingDots =
    knownLastPage !== null ? windowEnd < knownLastPage - 1 : hasMore !== false
  const showLastPageButton = knownLastPage !== null && windowEnd < knownLastPage

  const isKnownTotal = totalPages !== null

  // Desktop window (also the whole unknown-total view). Known-total marks
  // every item 'desktop' — its mobile view is rendered separately as
  // mobilePageItems, capped at 5 pages. Unknown-total stays compact on
  // mobile: page 1 and the current page only (WWW-5095).
  const windowPages: PageItem[] = Array.from(
    { length: windowEnd - windowStart + 1 },
    (_, i) => windowStart + i,
  )
    .filter(value => value !== 1) // page 1 is pinned separately
    .map(value => ({
      kind: 'page' as const,
      value,
      // Known-total: desktop-only (mobile renders its capped window via
      // buildMobilePageItems). Unknown-total: page 1 + current page show on
      // mobile too (WWW-5095).
      show: !isKnownTotal && mobilePages.has(value) ? ('always' as const) : ('desktop' as const),
    }))

  // Leading "…". Known-total: desktop-only (the capped mobile view has its
  // own); unknown-total: shown on mobile only when page 1 and the current
  // page aren't adjacent.
  let leftDotsVisibility: Visibility = 'mobile'
  if (windowStart > 1) leftDotsVisibility = 'always'
  if (isKnownTotal) leftDotsVisibility = 'desktop'

  const leadingDots: PageItem[] =
    currentPage > 2 ? [{ kind: 'dots', id: 'left', show: leftDotsVisibility }] : []
  const trailingDots: PageItem[] = showTrailingDots
    ? [{ kind: 'dots', id: 'right', show: isKnownTotal ? 'desktop' : 'always' }]
    : []
  const lastPageItem: PageItem[] = showLastPageButton
    ? [{ kind: 'page', value: knownLastPage as number, show: 'desktop' }]
    : []

  const pageItems: PageItem[] = [
    // Page 1 is pinned up front so the mobile "1 …" prefix always leads.
    { kind: 'page', value: 1, show: isKnownTotal ? 'desktop' : 'always' },
    ...leadingDots,
    ...windowPages,
    ...trailingDots,
    ...lastPageItem,
  ]

  const mobilePageItems: PageItem[] = isKnownTotal
    ? buildMobilePageItems(currentPage, knownLastPage as number)
    : []

  const isAtKnownLastPage = knownLastPage !== null && currentPage >= knownLastPage
  const nextDisabled = isAtKnownLastPage || isFetching
  const prevDisabled = currentPage === 1 || isFetching

  const startRecord = (currentPage - 1) * recordsPerPage + 1
  const endRecord = currentPage * recordsPerPage

  // Record-count line: "start - end of total" only for the known-total shape
  // (`totalRecords` passed — crypto offset API / baskets full list). The
  // cursor shape (stock/prediction) never shows "of total", even once the
  // backend is exhausted and the real total is knowable — it stays "x - y".
  const recordTotal = totalRecords ?? loadedPageCount * recordsPerPage
  const recordEnd = Math.min(endRecord, recordTotal)
  const countLine =
    totalPages !== null
      ? t('pagination', { start: startRecord, end: recordEnd, total: recordTotal })
      : t('paginationRange', { start: startRecord, end: recordEnd })

  const renderItem = (item: PageItem, keyPrefix: string) =>
    item.kind === 'page' ? (
      <MantinePagination.Control
        key={`${keyPrefix}${item.value}`}
        active={item.value === currentPage}
        visibleFrom={item.show === 'desktop' ? 'medium' : undefined}
        hiddenFrom={item.show === 'mobile' ? 'medium' : undefined}
        onClick={() => onChange(item.value)}
      >
        {item.value}
      </MantinePagination.Control>
    ) : (
      <MantinePagination.Dots
        key={`${keyPrefix}${item.id}`}
        visibleFrom={item.show === 'desktop' ? 'medium' : undefined}
        hiddenFrom={item.show === 'mobile' ? 'medium' : undefined}
      />
    )

  return (
    <Flex
      justify="space-between"
      align="center"
      gap="8px"
      // WWW-5152: mobile (< medium) always stacks — controls row on top,
      // count line below. column-reverse keeps the DOM order (text first)
      // so the desktop row stays text-left / controls-right.
      direction={{ base: 'column-reverse', medium: 'row' }}
    >
      <Text variant="body" c="var(--content-secondary)">
        {countLine}
      </Text>

      <MantinePagination.Root
        // Mantine requires total >= value. We lie about total — the real total
        // is unknown — so we just pick something ≥ the largest number we render.
        total={Math.max(1, loadedPageCount, currentPage, windowEnd, totalPages ?? 0)}
        value={currentPage}
        onChange={onChange}
        disabled={isFetching}
      >
        <Flex gap="4px" align="center">
          <MantinePagination.Previous
            disabled={prevDisabled}
            onClick={() => !prevDisabled && onChange(currentPage - 1)}
          />
          {pageItems.map(item => renderItem(item, ''))}
          {mobilePageItems.map(item => renderItem(item, 'm-'))}
          <MantinePagination.Next
            disabled={nextDisabled}
            onClick={() => !nextDisabled && onChange(currentPage + 1)}
          />
        </Flex>
      </MantinePagination.Root>
    </Flex>
  )
}
